import React, { Fragment, FunctionComponent, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  StepperContent,
  StepperHeader,
  StepperSubmit
} from '../../../../../shared/general/stepper';
import { utilsHomeValueGetDefaultValuesStep2 } from '../../utils';
import { FormElementDynamicInputTyped } from '../../../../../shared/form-elements';
import {
  CalculatorsHomeValueFormFields,
  CalculatorsHomeValueResult,
  CalculatorsHomeValueStep2FormFields
} from '../../../../../../@types/features/calculators';
import { useFormContext } from '../../../../../../hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { ResidenceType, useCalculatorHomeValueStep2FormFields } from '../../config';
import { HomeValueContext } from '../../context';
import { Heading } from '@chakra-ui/react';
import { HomeValueResidenceType } from '../../components/residence-type';
import HomeValueIconTussenwoning from '../../icons/home-value-icon-tussenwoning';
import HomeValueIconHoekhuis from '../../icons/home-value-icon-hoekhuis';
import HomeValueIconAppartement from '../../icons/home-value-icon-appartement';
import HomeValueIconTweeOnderEenKap from '../../icons/home-value-icon-twee-onder-een-kap';
import HomeValueIconVrijstaandeWoning from '../../icons/home-value-icon-vrijstaande-woning';
import HomeValueIconAndereWoning from '../../icons/home-value-icon-andere-woning';
import { validationMessagesTranslated } from '../../../../../shared/validation/validation-messages';

export const CalculatorHomeValueStepperStep2: FunctionComponent<{
  id: number;
  title?: string;
}> = ({ id, title }) => {
  const intl = useIntl();
  const calculatorHomeValueStep2FormFields = useCalculatorHomeValueStep2FormFields(intl);
  const [completedText, setCompletedText] = useState('');
  //const { getPropertyInformation } = homeValueUseResidenceValueHook();
  const { updateForm } = useFormContext<
    CalculatorsHomeValueResult,
    CalculatorsHomeValueFormFields
  >();
  const { propertyInformation, setSelectedResidenceType, setSelectedResidenceTypeLabel } =
    useContext(HomeValueContext);
  const residenceTypes = useCalculatorHomeValueStep2FormFields(intl).residenceType?.options;
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onBlur',
    defaultValues: utilsHomeValueGetDefaultValuesStep2(intl)
  });

  useEffect(() => {
    if (propertyInformation) {
      setValue(
        'residenceType',
        propertyInformation.houseType === 'Appartement'
          ? propertyInformation.houseType
          : propertyInformation.houseSubType,
        { shouldValidate: propertyInformation?.houseType !== undefined }
      );
      setValue(
        'energyLabel',
        propertyInformation.energyLabel ? propertyInformation.energyLabel : 'Unknown',
        { shouldValidate: propertyInformation?.energyLabel !== undefined }
      );
    }
  }, [propertyInformation]);

  const onSubmit = async (formData: Record<keyof CalculatorsHomeValueStep2FormFields, string>) => {
    const selectedResidenceTypeLabel = residenceTypes?.find(
      (type) => type.value === formData.residenceType
    )?.label;
    setCompletedText(selectedResidenceTypeLabel ? selectedResidenceTypeLabel : '');
    setSelectedResidenceTypeLabel(selectedResidenceTypeLabel ? selectedResidenceTypeLabel : '');
    updateForm(formData);
  };

  const handleOnClick = (defaultValue: ResidenceType) => {
    setSelectedResidenceType(defaultValue);
  };

  return (
    <Fragment>
      <StepperHeader
        title={title}
        id={id}
        onClose={handleSubmit(onSubmit)}
        completedText={completedText}
      />
      <StepperContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={`mb-5`}>
            <Heading size={`md`}>
              <FormattedMessage
                id={`step2.residenceType.title`}
                defaultMessage={`Wat zijn de specifieke kenmerken van de woning?`}
              />
            </Heading>
            <div className={`grid grid-cols-2 lg:grid-cols-3 gap-8`}>
              {residenceTypes?.map((option) => (
                <HomeValueResidenceType
                  register={register}
                  icon={getSelectedResidenceIcon(option.value)}
                  key={option.value}
                  checked={getValues('residenceType') === option.value}
                  defaultValue={option.value}
                  label={option.label}
                  name={`residenceType`}
                  onClick={handleOnClick}
                  id={option.value}
                />
              ))}
            </div>
            {/**<FormElementDynamicInputTyped<CalculatorsHomeValueStep2FormFields>
              formFields={calculatorHomeValueStep2FormFields}
              id={'residenceType'}
              register={register}
              errors={errors}
              values={watch()}
              options={{ required: true }}
            /> */}
          </div>

          <div className={`md:flex md:gap-10`}>
            <FormElementDynamicInputTyped<CalculatorsHomeValueStep2FormFields>
              formFields={calculatorHomeValueStep2FormFields.fields}
              id={'maintenance'}
              register={register}
              errors={errors}
              values={watch()}
              validation={{ required: validationMessagesTranslated(intl).required }}
            />

            <FormElementDynamicInputTyped<CalculatorsHomeValueStep2FormFields>
              formFields={calculatorHomeValueStep2FormFields.fields}
              id={'energyLabel'}
              register={register}
              errors={errors}
              values={watch()}
              validation={{ required: validationMessagesTranslated(intl).required }}
            />
          </div>

          <StepperSubmit id={id} isValidated={isValid} />
        </form>
      </StepperContent>
    </Fragment>
  );
};

function getSelectedResidenceIcon(type: ResidenceType) {
  switch (type) {
    case 'Appartement':
      return <HomeValueIconAppartement />;
    case 'Tussenwoning':
      return <HomeValueIconTussenwoning />;
    case 'Hoekwoning':
      return <HomeValueIconHoekhuis />;
    case 'TweeOnderEinKapwoning':
      return <HomeValueIconTweeOnderEenKap />;
    case 'VrijstaandeWoning':
      return <HomeValueIconVrijstaandeWoning />;
    case 'Woonhuis':
      return <HomeValueIconVrijstaandeWoning />;
    default:
      return <HomeValueIconAndereWoning />;
  }
}
